/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import "../SelectMission.css";
import PropTypes from "prop-types";
import MissionItem from "./MissionItem";
import QuickPayment from "./QuickPayment";
import { QRCodeCanvas } from "qrcode.react";
import InfiniteScroll from "react-infinite-scroll-component";
import { Link } from "react-router-dom";
import { renderSelectML } from "../../../assets/component/renderSelectML.js";
import checkplusLogo from "../../../assets/component/images/checkplus-logo.svg";
import plugLogo from "../../../assets/component/images/plug-logo.svg";
import { PLATFORM, ENVIRONMENT } from "../../../config";
import { getTextColor, resolveThemeColors } from "../../../assets/helper.js";

import { simpleFetch } from "../../../restWrapper";

class SelectMissionContent extends Component {
  constructor(props) {
    super(props);
    // Copy specific fields from props.state, but manage our own fetching state for projects
    // Don't spread ...this.props.state as it would copy parent's fetching: true
    this.state = {
      subdomain: this.props.state.subdomain,
      domain: this.props.state.domain,
      isCustomDomain: this.props.state.isCustomDomain,
      missions: this.props.state.missions || [],
      lastId: this.props.state.lastId || 0,
      hasMore: this.props.state.hasMore !== undefined ? this.props.state.hasMore : true,
      fetching: false, // Child manages its own fetching state for projects
      showQuickPayment: this.props.state.isQuickPaymentFlow || this.props.state.isNoProjectFlow || false,
      debugNoProjects: false,
      headerOpacity: 1, // State property for header opacity
      isScrolling: false
    };
    this.toggleQuickPayment = this.toggleQuickPayment.bind(this);
    this.getProjectData = () => {
      // Don't fetch if we're already fetching or know there are no more projects
      if (!this.state.subdomain || this.state.fetching || (this.state.hasMore === false && this.state.missions.length > 0)) {
        return Promise.resolve(null);
      }

      this.setState({ fetching: true });
      return simpleFetch("/projects", "GET", {
        subDomain: this.state.isCustomDomain ? this.state.domain : this.state.subdomain,
        isCustomDomain: this.state.isCustomDomain,
        lastId: this.state.lastId,
        status: "open",
      })
        .then((res) => {
          if (res.status !== 200) {
            this.props.history.push({ pathname: "/" + this.props.language });
            return res.status;
          }

          // No new projects found
          if (!res.body || res.body.length === 0) {
            this.setState(prevState => ({
              ...prevState,
              hasMore: false,
              fetching: false
              // Keep existing missions, don't reset to empty array
            }));
            return res.status;
          }

          const lastItem = res.body[res.body.length - 1];
          
          this.setState(prevState => ({
            ...prevState,
            missions: prevState.missions.concat(res.body),
            lastId: lastItem.id,
            hasMore: true,
            fetching: false
          }));

          return res.status;
        })
        .catch(error => {
          // console.error("Error fetching projects:", error);
          this.setState({ fetching: false });
          return null;
        });
    };
  }

  componentDidMount() {
    // Don't set fetching:true here - getProjectData handles that itself
    // Setting it here prevents getProjectData from running (it checks this.state.fetching)
    this.setState({
      isScrolling: false,
    }, () => {
      this.getProjectData();
    });
    window.scrollTo(0, 0);
    
    // Add scroll handler for isScrolling state and header opacity
    this.scrollHandler = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      // Get the header element
      const headerElement = document.querySelector('.SelectMission-header');

      if (headerElement) {
        const headerHeight = headerElement.offsetHeight;

        // Calculate header opacity based on scroll position
        const maxScrollForEffects = 200; // Scroll range over which opacity changes
        const minOpacity = 0.5; // Minimum opacity value

        let opacity = 1;

        if (scrollTop > 0) {
          // Calculate opacity based on scroll position
          opacity = 1 - (scrollTop / maxScrollForEffects) * (1 - minOpacity);
          // Ensure opacity doesn't go below minimum
          opacity = Math.max(opacity, minOpacity);
        }

        const newIsScrolling = scrollTop > headerHeight;

        // Only update state if values have actually changed to prevent unnecessary re-renders
        if (Math.abs(this.state.headerOpacity - opacity) > 0.01 || this.state.isScrolling !== newIsScrolling) {
          this.setState({
            headerOpacity: opacity,
            isScrolling: newIsScrolling
          });
        }
      } else {
        // Fallback to original behavior if header element is not found
        const newIsScrolling = scrollTop > 0;
        if (this.state.isScrolling !== newIsScrolling || this.state.headerOpacity !== 1) {
          this.setState({
            isScrolling: newIsScrolling,
            headerOpacity: 1
          });
        }
      }
    };
    window.addEventListener("scroll", this.scrollHandler);
  }

  componentWillUnmount() {
    // Clean up scroll handler
    if (this.scrollHandler) {
      window.removeEventListener("scroll", this.scrollHandler);
    }
  }

  toggleQuickPayment() {
    if (this.props.onToggleQuickPayment) {
      this.props.onToggleQuickPayment();
    }
  }

  // Assuming the subscription plan is stored in this.props.userSubscriptionPlan
  // and the plans are 'basic', 'premium', and 'enterprise' with limits 5, 10, and unlimited respectively.
  
  getMissionLimit() {
    const userSubscriptionPlan = this.props.state.currentPlan;
    switch (userSubscriptionPlan) {
      case "legacy":
        return 1;
      case "plus":
        return 3;
      case "pro":
        return 5;
      case "premium":
        return this.state.missions.length; // No limit for enterprise
      default:
        return 0; // Default limit if no subscription plan is found
    }
  }

  renderStepsContainer(isQuickPayment = false) {
    const languageObj = this.props.languageObj;
    const isFreePlan = this.props.state.currentPlan === "free";
    const headerOverrideStyle = {
      background: "#" + this.props.state.headerColor,
    };
    const logoStyle = this.props.logoStyle;

    // Custom style to remove shadow when in Quick Payment mode
    const containerStyle = isQuickPayment ? {
      boxShadow: 'none'
    } : {};

    const stepsContainerClass = isFreePlan
      ? "SelectMission-stepsContainer SelectMission-stepsContainer--free"
      : "SelectMission-stepsContainer";
    const stepTextClass = isFreePlan
      ? "SelectMission-stepTxt SelectMission-stepTxt--free"
      : "SelectMission-stepTxt";
    const indicatorClass = isFreePlan
      ? "SelectMission-indicator SelectMission-indicator--free"
      : "SelectMission-indicator";

    return (
      <div className={stepsContainerClass} style={containerStyle}>
        <div className="SelectMission-stepsContent">
          <div className={stepTextClass}>
            {this.state.missions.length === 0 || isQuickPayment
              ? (languageObj.step1of4 || "Step 1 of 4")
              : languageObj.stepTxt}
          </div>
          <div className={indicatorClass}>
            {this.state.missions.length === 0 || isQuickPayment ? (
              /* 4 steps for no-project flow and Quick Payment */
              <>
                <div className="SelectMission-circle SelectMission-circleSelected SelectMission-marginRight" />
                <div className="SelectMission-circle SelectMission-circleEmpty SelectMission-marginRight" />
                <div className="SelectMission-circle SelectMission-circleEmpty SelectMission-marginRight" />
                <div className="SelectMission-circle SelectMission-circleEmpty" />
              </>
            ) : (
              /* 5 steps for regular flow */
              <>
                <div className="SelectMission-circle SelectMission-circleSelected SelectMission-marginRight" />
                <div className="SelectMission-circle SelectMission-circleEmpty SelectMission-marginRight" />
                <div className="SelectMission-circle SelectMission-circleEmpty SelectMission-marginRight" />
                <div className="SelectMission-circle SelectMission-circleEmpty SelectMission-marginRight" />
                <div className="SelectMission-circle SelectMission-circleEmpty" />
              </>
            )}
          </div>
        </div>

        {!isFreePlan ? (
          this.state.isScrolling ? (
            <div
              className="SelectMission-smallLogoContainer SelectMission-fadeIn"
              style={headerOverrideStyle}
            >
              <div className="SelectMission-logoS" style={logoStyle}></div>
            </div>
          ) : (
            <div></div>
          )
        ) : null}
      </div>
    );
  }

  render() {
    const missionLimit = this.getMissionLimit();

    // Check both parent's organization loading and child's project loading
    // Parent checks props.state.fetching (organization data from SelectMission)
    // Child checks this.state.fetching (project data from getProjectData)
    const isInitialLoading = this.props.state.fetching || (this.state.fetching && this.state.missions.length === 0);

    if (
      isInitialLoading ||
      !this.state.subdomain
    ) {
      return (
        <div className="MissionDetails-view MissionDetails-gradient">
          <div className="MissionDetails-header  MissionDetails-gradient">
            <div className="MissionDetails-logo MissionDetails-gradient"></div>

            <div className="MissionDetails-mainToolBarContainer">
              <div className="MissionDetails-bg" />
            </div>
          </div>

          <div className="MissionDetails-loadingSteps">
            <div className="MissionDetails-loadingIndicator MissionDetails-gradient"></div>
          </div>

          <div className="SelectMission-scrollContainer MissionDetails-gradient"></div>
        </div>
      );
    }

    const freeBrandColor = "#4A9FE9";
    const freeIconBackground = "#E3F1FF";

    const isFreePlan = this.props.state.currentPlan === "free";

    const headerOverrideStyle = isFreePlan
      ? { background: freeBrandColor }
      : { background: "#" + this.props.state.headerColor };

    const toolbarBgStyle = isFreePlan
      ? { background: "#FFFFFF" }
      : { background: "#" + this.props.state.headerColor };

    const resolved = resolveThemeColors(this.props.state.headerColor, this.props.state.buttonColor);
    const resolvedHeaderStyle = isFreePlan
      ? { background: freeBrandColor }
      : { background: "#" + resolved.headerColor };

    const buttonOverrideStyle = isFreePlan
      ? { background: freeIconBackground }
      : { background: "#" + resolved.buttonColor };

    const buttonOverrideTextStyle = isFreePlan
      ? { color: freeBrandColor }
      : { color: getTextColor(resolved.buttonColor) };

    const languageObj = this.props.languageObj;

    const qrUrl = window.location.href;

    const logoStyle = this.props.logoStyle;

    const organizationName = this.props.organizationName;

    const scrollBarStyle = {
      overflow: "hidden",
    };

    const RenderMissionLoadingIcon = () => {
      if (this.state.fetching)
        return (
          <div
            className="SelectMission-btm-load"
            style={headerOverrideStyle}
          ></div>
        );
      return null;
    };

    // Show language selector only if organization has multiple supported languages
    const organization = this.props.state.organization || {};
    const supportedLanguages = organization.supportedLanguages || [];
    const currentPlan = organization.currentPlan || '';
    const isPremiumOrPro = currentPlan === 'premium' || currentPlan === 'pro';
    const shouldShowLanguageSelector = supportedLanguages.length > 1 && isPremiumOrPro;

    const RenderTopBar = () => {
      if (!shouldShowLanguageSelector) return null;
      return (
        <div
          className="SelectMission-languageSelector"
          style={buttonOverrideStyle}
        >
          <div
            className="SelectMission-globe"
            style={resolvedHeaderStyle}
          />
          {renderSelectML(buttonOverrideStyle, buttonOverrideTextStyle, supportedLanguages)}
        </div>
      );
    };

    const color = this.props.color;
    // Removed console.log from render method to prevent spam on every re-render
    // console.log(JSON.stringify(color) + " -143");

    const loginWrapperClass = isFreePlan
      ? "SelectMission-RenderLogin SelectMission-RenderLogin--free"
      : "SelectMission-RenderLogin";
    const loginButtonClass = isFreePlan
      ? "SelectMission-loginBtnContainer SelectMission-loginBtnContainer--free"
      : "SelectMission-loginBtnContainer";
    const recordButtonClass = isFreePlan
      ? "SelectMission-recordContainer SelectMission-recordContainer--free"
      : "SelectMission-recordContainer";

    const shouldShowQuickPayment = isFreePlan || this.props.state.showQuickPayment;

    const RenderLogin = () => {
      if (this.props.isAuth) {
        return (
          <div className={loginWrapperClass}>
            <Link
              className={loginButtonClass}
              style={buttonOverrideStyle}
              to={{
                pathname: "/AcInfo",
                state: {
                  logoStyle: logoStyle,
                  color: color,
                  organizationName: organizationName,
                },
              }}
            >
              <div
                className="SelectMission-userIcn"
                style={resolvedHeaderStyle}
              ></div>
            </Link>

            <Link
              to={{
                pathname: "/Transactions",
                state: {
                  ...this.state,
                },
              }}
              className={recordButtonClass}
              style={buttonOverrideStyle}
            >
              <div
                className="SelectMission-recordIcon"
                style={resolvedHeaderStyle}
              ></div>
            </Link>

            <Link
              className={recordButtonClass}
              style={buttonOverrideStyle}
              to={{
                pathname: "/ListCards",
                state: {
                  ...this.state,
                  ...{
                    color: color,
                    subDomain: this.state.subDomain,
                    organizationName: organizationName,
                    // cardPaymentsStatus: cardPaymentsStatus,
                    // transfersStatus: transfersStatus,
                  },
                },
              }}
            >
              <div
                className="SelectMission-cardIcn"
                style={resolvedHeaderStyle}
              ></div>
            </Link>
          </div>
        );
      }

      return (
        <Link
          className={loginButtonClass}
          style={buttonOverrideStyle}
          to={{
            pathname: "/LoginPrompt",
            state: {
              logoStyle,
            },
          }}
        >
          <div
            className="SelectMission-userIcn"
            style={resolvedHeaderStyle}
          ></div>
        </Link>
      );
    };

    const RenderHeader = () => {
      if (isFreePlan) {
        return (
          <div className="SelectMission-header SelectMission-header--free">
            <div className="SelectMission-freeBrand">
              <span className="SelectMission-freeBrandPowered">Powered by</span>
              <img
                src={PLATFORM === "plug" ? plugLogo : checkplusLogo}
                alt={PLATFORM === "plug" ? "Plug" : "CheckPlus for Nonprofits"}
                className="SelectMission-freeBrandLogoImage"
              />
            </div>
            <div className="SelectMission-freeHeaderStepsWrapper">
              {this.renderStepsContainer(this.state.missions.length === 0 || shouldShowQuickPayment)}
            </div>
            <div className="SelectMission-mainToolBarContainer SelectMission-mainToolBarContainer--free">
              <div className="SelectMission-bg" style={toolbarBgStyle} />
              <RenderLogin />

              {(organization.currentPlan === "premium" || organization.currentPlan === "pro") && (<RenderTopBar />)}
            </div>
          </div>
        );
      }

      return (
        <div className="SelectMission-header" style={headerOverrideStyle}>
          <div className="SelectMission-logo" style={logoStyle}>
            {!logoStyle ? "L O G O" : ""}
          </div>

          <div className="SelectMission-mainToolBarContainer">
            <div className="SelectMission-bg" style={headerOverrideStyle} />
            <RenderLogin />

            {(organization.currentPlan === "premium" || organization.currentPlan === "pro") && (<RenderTopBar />)}
          </div>

          <div className="SelectMission-QR">
            <div className="SelectMission-centerQR">
              <QRCodeCanvas
                style={{
                  height: "100%",
                  width: "100%",
                }}
                value={qrUrl}
              />
            </div>
          </div>
        </div>
      );
    };

    const freePlanFlowFlags = isFreePlan ? { isFreePlanFlow: true } : undefined;

    // Render QuickPayment outside of SelectMission-main
    const renderQuickPaymentOutsideMain = () => {
      // For free users, always render QuickPayment
      if (isFreePlan) {
        return (
          <QuickPayment
            organization={this.props.state.organization || {}}
            organizationName={organizationName}
            headerColor={freeBrandColor.replace('#','')}
            orgIntroHeaderColor={this.props.state.headerColor}
            buttonColor={'FFFFFF'}
            minimumPaymentAmount={(this.props.state.organization && this.props.state.organization.minimumPaymentAmount) || 0}
            languageObj={languageObj}
            history={this.props.history}
            subdomain={this.state.subdomain}
            language={this.props.language}
            logoStyle={logoStyle}
            organizationLogo={this.props.state.logo}
            missions={[]}
            amount={this.props.state.amount}
            organizationId={this.props.state.organizationId}
            organizationViewId={this.props.state.organizationViewId}
            coverFee={typeof this.props.state.coverFee === 'boolean' ? this.props.state.coverFee : false}
            enableProjectSelection={false}
            showOrganizationIntro={true}
            stepsContainer={null}
            domain={this.state.domain}
            isCustomDomain={this.state.isCustomDomain}
            flowFlags={freePlanFlowFlags}
            onSwitchToStandardMode={this.props.onSwitchToStandardMode}
            isAuth={this.props.isAuth}
          />
        );
      }

      // For paid users with no projects (no-project flow)
      if (this.state.missions.length === 0 || this.state.debugNoProjects) {
        return (
          <QuickPayment
            organization={this.props.state.organization || {}}
            organizationName={organizationName}
            headerColor={this.props.state.headerColor}
            orgIntroHeaderColor={this.props.state.headerColor}
            buttonColor={this.props.state.buttonColor}
            minimumPaymentAmount={(this.props.state.organization && this.props.state.organization.minimumPaymentAmount) || 0}
            languageObj={languageObj}
            history={this.props.history}
            subdomain={this.state.subdomain}
            language={this.props.language}
            logoStyle={logoStyle}
            organizationLogo={this.props.state.logo}
            missions={[]}
            amount={this.props.state.amount}
            organizationId={this.props.state.organizationId}
            organizationViewId={this.props.state.organizationViewId}
            coverFee={typeof this.props.state.coverFee === 'boolean' ? this.props.state.coverFee : false}
            enableProjectSelection={false}
            title={this.props.state.quickDonationTitle || (languageObj.generalDonationTitle || "General Donation")}
            showOrganizationIntro={false}
            stepsContainer={this.renderStepsContainer(true)}
            domain={this.state.domain}
            isCustomDomain={this.state.isCustomDomain}
            flowFlags={{
              isQuickPaymentFlow: false,
              isNoProjectFlow: true
            }}
            isAuth={this.props.isAuth}
            onSwitchToStandardMode={this.props.onSwitchToStandardMode}
          />
        );
      }

      // For paid users with projects, only render when QuickPayment toggle is on
      if (!shouldShowQuickPayment) {
        return null;
      }

      // Paid plan QuickPayment (with projects)
      return (
        <QuickPayment
          organization={this.props.state.organization || {}}
          organizationName={organizationName}
          headerColor={this.props.state.headerColor}
          orgIntroHeaderColor={this.props.state.headerColor}
          buttonColor={this.props.state.buttonColor}
          minimumPaymentAmount={(this.props.state.organization && this.props.state.organization.minimumPaymentAmount) || 0}
          languageObj={languageObj}
          history={this.props.history}
          subdomain={this.state.subdomain}
          language={this.props.language}
          logoStyle={logoStyle}
          organizationLogo={this.props.state.logo}
          missions={this.state.missions}
          amount={this.props.state.amount}
          organizationId={this.props.state.organizationId}
          organizationViewId={this.props.state.organizationViewId}
          coverFee={typeof this.props.state.coverFee === 'boolean' ? this.props.state.coverFee : false}
          enableProjectSelection={true}
          showOrganizationIntro={false}
          stepsContainer={this.renderStepsContainer(true)}
          domain={this.state.domain}
          isCustomDomain={this.state.isCustomDomain}
          flowFlags={freePlanFlowFlags}
          onSwitchToStandardMode={this.props.onSwitchToStandardMode}
          isAuth={this.props.isAuth}
        />
      );
    };

    // Hide SelectMission-main when QuickPayment is shown OR when there are no projects
    // SelectMission-main is ONLY used for showing project list (paid users with projects, default view)
    const shouldHideMainForQuickPayment = isFreePlan || shouldShowQuickPayment || this.state.missions.length === 0 || this.state.debugNoProjects;

    return (
      <div>
        {/* Debug Toolbar - only in local/dev environments, show with ?debug=true */}
        {['local', 'dev'].includes(ENVIRONMENT) && new URLSearchParams(window.location.search).has('debug') && (
          <div style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            background: '#1a1a2e',
            padding: '8px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            borderTop: '2px solid #e94560',
            fontFamily: 'monospace',
            fontSize: '12px',
            color: '#eee',
          }}>
            <span style={{ color: '#e94560', fontWeight: 'bold' }}>DEBUG</span>
            <button
              onClick={() => this.setState(prev => ({ debugNoProjects: !prev.debugNoProjects }))}
              style={{
                background: this.state.debugNoProjects ? '#e94560' : '#333',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                padding: '4px 10px',
                cursor: 'pointer',
                fontFamily: 'monospace',
                fontSize: '12px',
              }}
            >
              No Projects {this.state.debugNoProjects ? 'ON' : 'OFF'}
            </button>
          </div>
        )}

        <div className="SelectMission-bigHeader" style={{
          opacity: this.state.headerOpacity,
          transition: 'opacity 0.3s ease'
        }}>
          <RenderHeader />
        </div>

        {/* QuickPayment - rendered outside SelectMission-main for both paid and free users */}
        {renderQuickPaymentOutsideMain()}

        {/* SelectMission-main is ONLY shown for paid users with projects (default view, not QuickPayment) */}
        {!shouldHideMainForQuickPayment && (
          <div className="SelectMission-main">
            {/* Show steps header for paid users with projects */}
            {this.renderStepsContainer()}

            <InfiniteScroll
              dataLength={this.state.missions.length}
              next={this.getProjectData}
              hasMore={this.state.hasMore}
              style={scrollBarStyle}
            >
              <div className="SelectMission-scrollContainer">
                {/* Quick Payment Toggle Button - Now inside scrollContainer */}
                {this.state.missions.length > 0 && !isFreePlan && (
                  <div className="SelectMission-quickPaymentToggle SelectMission-quickPaymentToggleInScroll">
                    <button
                      onClick={this.toggleQuickPayment}
                      style={{
                        ...(this.state.showQuickPayment ? buttonOverrideStyle : { background: "#" + resolved.headerColor }),
                        padding: '10px 20px',
                        borderRadius: '20px',
                        border: 'none',
                        color: this.state.showQuickPayment ? '#' + resolved.headerColor : '#' + resolved.buttonColor,
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}
                    >
                      {this.state.showQuickPayment
                        ? (this.props.state.organization && this.props.state.organization.allowedTransactionTypes === 'donation'
                          ? (languageObj.hideQuickDonation || "Hide Quick Donation")
                          : (languageObj.hideQuickPayment || "Hide Quick Payment"))
                        : (this.props.state.organization && this.props.state.organization.allowedTransactionTypes === 'donation'
                          ? (languageObj.quickDonation || "Quick Donation")
                          : (languageObj.quickPayment || "Quick Payment"))}
                    </button>
                  </div>
                )}
                
                {this.state.missions.slice(0, missionLimit).map((mission) => (
                  <MissionItem
                    key={mission.id}
                    languageObj={languageObj}
                    mission={mission}
                    language={this.props.language}
                    progressBarColor={this.props.state.progressBarColor}
                    headerColor={this.props.state.headerColor}
                    buttonColor={this.props.state.buttonColor}
                    organizationName={organizationName}
                    organizationUrl={this.props.state.organizationUrl}
                    location={{state: this.props.state}}
                    // cardPaymentsStatus={cardPaymentsStatus}
                    // transfersStatus={transfersStatus}
                    // charges_enabled={charges_enabled}
                  />
                ))}
              </div>
            </InfiniteScroll>
            <RenderMissionLoadingIcon />
          </div>
        )}
      </div>
    );
  }
}

SelectMissionContent.propTypes = {
  color: PropTypes.object,
  // cardPaymentsStatus: PropTypes.string,
  // transfersStatus: PropTypes.string,
  // charges_enabled: PropTypes.string,
  language: PropTypes.string,
  state: PropTypes.object,
  languageObj: PropTypes.object,
  logoStyle: PropTypes.object,
  isAuth: PropTypes.bool,
  buttonOverrideStyle: PropTypes.object,
  textOverrideGroupStyle: PropTypes.object,
  buttonOverrideGroupStyle: PropTypes.object,
  buttonOverrideTextStyle: PropTypes.object,
  organizationName: PropTypes.string,
  onToggleQuickPayment: PropTypes.func,
  onSwitchToStandardMode: PropTypes.func,
  // userSubscriptionPlan: PropTypes.string.isRequired,
};
export default SelectMissionContent;
